'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import type { CardProps } from '@/types/card';
import { Card } from '@/components/raven/Card';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import type { CustomPostEntity } from '@/types/blocks';
import { Play, Pause } from '@themaven-net/raven-assets/index';
import { getPrimaryCategory } from '@/utils/post/get-primary-category';
import { useCarouselControls } from '@/utils/hooks/use-carousel-controls';
import { CarouselControlTextButton } from '@/components/raven/content/Carousels/Controls';

import styles from './styles.module.css';

interface CarouselSlideProps {
	centerContent: boolean;
	className?: string;
	displayByline: boolean;
	displayPromoTitle: boolean;
	displayTaxonomyTerms: boolean;
	index: number;
	isCarousel: boolean;
	mediaType: 'image' | 'video';
	post: CustomPostEntity;
	promoTitle: string;
}

const CarouselSlide: FC<CarouselSlideProps> = ({
	centerContent,
	className,
	displayByline,
	displayPromoTitle,
	displayTaxonomyTerms,
	isCarousel,
	mediaType,
	post,
	promoTitle,
}) => {
	const hasAuthors =
		post.authors &&
		post.authors.length > 0 &&
		!post.meta?.hide_author_from_article;
	const authors = hasAuthors
		? post.authors.map((author) => author.name).join(', ')
		: '';
	const showDate = displayByline && Boolean(post.date_gmt);
	const title = displayPromoTitle ? promoTitle : post.title;

	const cx = clsx(
		styles.carouselArticleCardFullBleedMediaBlock,
		'carousel__slide',
	);

	const cardProps = {
		author: displayByline && hasAuthors ? authors : '',
		category: displayTaxonomyTerms
			? getPrimaryCategory(post.categories, post.meta?.primary_category_id)
					?.name || ''
			: undefined,
		centerContent: centerContent || false,
		className: className || 'is-style-default is-style-carousel',
		date: showDate ? post.date_gmt : '',
		id: post.id,
		image: post?.featured_media || null,
		isCarousel,
		link: post.link,
		mediaType: mediaType || 'image',
		meta: post.meta || {},
		title: title || '',
		variation: 'full-bleed-media',
		video: post?.featured_video || null,
	} satisfies CardProps;

	return (
		<div className={cx}>
			<Card {...cardProps} key={post.id} />
		</div>
	);
};

export interface ArticleCardFullBleedMediaBlockAttributes {
	centerContent?: boolean;
	className?: string;
	displayByline: boolean;
	displayPromoTitle: boolean;
	displayTaxonomyTerms: boolean;
	mediaType?: 'image' | 'video';
	posts: CustomPostEntity[];
	promoTitle?: string;
}

export const ArticleCardFullBleedMedia: FC<
	ArticleCardFullBleedMediaBlockAttributes
> = ({
	centerContent,
	className: blockClassName,
	displayByline,
	displayPromoTitle,
	displayTaxonomyTerms,
	mediaType,
	posts,
	promoTitle,
}) => {
	const [carouselRef, carouselApi] = useEmblaCarousel(
		{
			align: 'start',
			loop: true,
			slidesToScroll: 1,
			startIndex: 0,
		},
		[Autoplay()],
	);

	const {
		autoplayState,
		onTextButtonClick,
		scrollSnaps,
		selectedIndex,
		toggleAutoplay,
	} = useCarouselControls(carouselApi);

	return posts.length > 1 ? (
		<div className={styles.articleCardFullBleedMediaBlock}>
			<div className="carousel">
				<div className="carousel__viewport" ref={carouselRef}>
					<div
						className={clsx(
							styles.articleCardFullBleedMediaBlockWrapperContainer,
							'carousel__container',
						)}
					>
						{posts.map((post, index) => (
							<CarouselSlide
								centerContent={centerContent ?? false}
								className={blockClassName ?? ''}
								displayByline={displayByline}
								displayPromoTitle={posts.length === 1 && displayPromoTitle}
								displayTaxonomyTerms={displayTaxonomyTerms}
								index={index}
								isCarousel
								key={post.id}
								mediaType={mediaType ?? 'image'}
								post={post}
								promoTitle={promoTitle ?? ''}
							/>
						))}
					</div>
				</div>

				<div
					className={clsx(
						'carousel__controls',
						styles.articleCardFullBleedMediaBlockCarouselControls,
					)}
				>
					<ul className="carousel__pagination">
						{scrollSnaps.map((_, index) => (
							<li key={`text-button-${scrollSnaps[index]}`}>
								<CarouselControlTextButton
									active={index === selectedIndex}
									aria-label={`Go to slide ${index + 1}`}
									onClick={() => onTextButtonClick(index)}
								>
									{index < 9 ? `0${index + 1}` : index + 1}
								</CarouselControlTextButton>
							</li>
						))}
					</ul>
					<button
						aria-label="Play/Pause"
						className={styles.articleCardFullBleedMediaBlockCarouselButton}
						onClick={() => {
							toggleAutoplay();
						}}
						type="button"
					>
						{autoplayState === 'play' ? (
							<Pause
								aria-hidden="true"
								className={
									styles.articleCardFullBleedMediaBlockCarouselButtonIcon
								}
								height={16}
								width={16}
							/>
						) : (
							<Play
								aria-hidden="true"
								className={
									styles.articleCardFullBleedMediaBlockCarouselButtonIcon
								}
								height={16}
								width={16}
							/>
						)}
					</button>
				</div>
			</div>
		</div>
	) : (
		<div className={styles.articleCardFullBleedMediaBlock}>
			<CarouselSlide
				centerContent={centerContent ?? false}
				className={blockClassName ?? ''}
				displayByline={displayByline}
				displayPromoTitle={displayPromoTitle}
				displayTaxonomyTerms={displayTaxonomyTerms}
				index={0}
				isCarousel={false}
				key={posts[0].id}
				mediaType={mediaType ?? 'image'}
				post={posts[0]}
				promoTitle={promoTitle ?? ''}
			/>
		</div>
	);
};
